import { Check, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ElementType, type ReactNode } from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@/lib/utils';
import { RecordOpener, registerSheetOpener } from './RecordOpener';

export interface ActionSheetItem {
	id?: string;
	label: ReactNode;
	/** A lucide icon component (or any component taking className). */
	icon?: ElementType;
	/** A second line, e.g. why the action is disabled. */
	description?: ReactNode;
	destructive?: boolean;
	disabled?: boolean;
	/** Shows a check on the right (pickers, toggles). */
	checked?: boolean;
	/** A value or badge on the right. */
	trailing?: ReactNode;
	/** Opens one of the sheet's `pages` instead of running onSelect. */
	page?: string;
	onSelect?: () => void;
	/** Keep the sheet open after onSelect, for multi-select pickers. */
	keepOpen?: boolean;
	/**
	 * Run onSelect once the sheet has closed and released focus, for actions
	 * that focus a field or open another sheet or dialog.
	 */
	afterClose?: boolean;
	/** Text the page search matches; defaults to the label when it is a string. */
	searchText?: string;
}

export interface ActionSheetSection {
	id?: string;
	title?: ReactNode;
	items: ActionSheetItem[];
}

export interface ActionSheetPage {
	id: string;
	title: ReactNode;
	sections?: ActionSheetSection[];
	/** Free content instead of (or above) sections. */
	content?: ReactNode;
	/** Adds a search field that filters the page's items. */
	searchable?: boolean;
	searchPlaceholder?: string;
	emptyText?: ReactNode;
}

export interface ActionSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Read by screen readers; shown small and centred unless `hideTitle`. */
	title: ReactNode;
	description?: ReactNode;
	hideTitle?: boolean;
	/** Custom block above the actions: quick reactions, a message preview. */
	header?: ReactNode;
	/** The root page's actions. `items` is shorthand for one untitled section. */
	sections?: ActionSheetSection[];
	items?: ActionSheetItem[];
	/** Drill-in pages (Assign to…, Move to folder…) opened by an item's `page`. */
	pages?: ActionSheetPage[];
	/** The separate Cancel button; false to leave it out. */
	cancelLabel?: ReactNode | false;
}

const itemText = (item: ActionSheetItem) =>
	(item.searchText ?? (typeof item.label === 'string' ? item.label : '')).toLowerCase();

/**
 * The phone replacement for dropdown menus, context menus and row "…"
 * menus: an iOS-style action sheet that slides up from the bottom, with
 * grouped 52px rows, a red destructive style, drill-in pages with a back
 * button and an optional search, and a separate Cancel button.
 *
 * Render it only on phones (keep the DropdownMenu on desktop). Every action
 * that a swipe or long-press offers should also be reachable from a visible
 * button that opens this sheet, for VoiceOver and keyboard users.
 */
export function ActionSheet({
	open,
	onOpenChange,
	title,
	description,
	hideTitle = false,
	header,
	sections,
	items,
	pages = [],
	cancelLabel = 'Cancel',
}: ActionSheetProps) {
	const [stack, setStack] = useState<string[]>([]);
	const [query, setQuery] = useState('');
	const contentRef = useRef<HTMLDivElement>(null);
	const returnFocus = useRef<HTMLElement | null>(null);
	const pendingAction = useRef<(() => void) | null>(null);

	// Hand focus back to the opener unless an action already moved it (into a
	// dialog or a field), then run a pending afterClose action from there, so a
	// sheet or dialog it opens hands focus back to the same button.
	const handBack = () => {
		const action = pendingAction.current;
		const opener = returnFocus.current;
		pendingAction.current = null;
		returnFocus.current = null;
		const active = document.activeElement;
		const lost = !active || active === document.body || Boolean(contentRef.current?.contains(active));
		if (lost && opener?.isConnected) opener.focus({ preventScroll: true });
		action?.();
	};

	useEffect(() => {
		if (open) {
			setStack([]);
			setQuery('');
		}
	}, [open]);

	const page = stack.length ? pages.find((p) => p.id === stack[stack.length - 1]) : undefined;
	const showDescription = Boolean(description) && !page;
	const rootSections = sections ?? (items ? [{ items }] : []);
	const visibleSections = useMemo(() => {
		const list = page ? page.sections ?? [] : rootSections;
		const q = query.trim().toLowerCase();
		if (!page?.searchable || !q) return list;
		return list
			.map((section) => ({ ...section, items: section.items.filter((item) => itemText(item).includes(q)) }))
			.filter((section) => section.items.length > 0);
	}, [page, rootSections, query]);

	const select = (item: ActionSheetItem) => {
		if (item.disabled) return;
		if (item.page) {
			setStack((s) => [...s, item.page as string]);
			setQuery('');
			return;
		}
		if (item.afterClose && !item.keepOpen) {
			const action = item.onSelect ?? null;
			pendingAction.current = action;
			// Normally run by onCloseAutoFocus; don't lose it if that never fires
			// (or a slow phone is still animating the close).
			setTimeout(() => {
				if (pendingAction.current === action) handBack();
			}, 700);
		} else item.onSelect?.();
		if (!item.keepOpen) onOpenChange(false);
	};

	return (
		<DrawerPrimitive.Root open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
				<DrawerPrimitive.Content
					ref={(node) => {
						contentRef.current = node;
						registerSheetOpener(node, returnFocus);
					}}
					data-opener-sheet=""
					// No description: leave aria-describedby off (VoiceOver would read the title twice).
					{...(showDescription ? {} : { 'aria-describedby': undefined })}
					onCloseAutoFocus={(event) => {
						event.preventDefault();
						handBack();
					}}
					onOpenAutoFocus={(event) => {
						event.preventDefault();
						(event.currentTarget as HTMLElement | null)?.focus?.();
					}}
					className="fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(var(--app-height)-var(--safe-top)-var(--call-bar-height,0px)-0.75rem)] flex-col rounded-t-[14px] bg-zinc-100 outline-none dark:bg-zinc-950"
				>
					{/* What opened the sheet (usually a plain button, not a Drawer.Trigger), to hand focus back on Cancel. */}
					<RecordOpener into={returnFocus} />
					<div aria-hidden className="mx-auto mt-2 h-[5px] w-9 shrink-0 rounded-full bg-muted-foreground/30" />
					{page ? (
						<div className="grid h-12 shrink-0 grid-cols-[1fr_auto_1fr] items-center px-1.5">
							<button
								type="button"
								onClick={() => {
									setStack((s) => s.slice(0, -1));
									setQuery('');
								}}
								className="inline-flex h-11 items-center justify-self-start rounded-lg pr-2 text-[17px] text-primary active:opacity-50"
							>
								<ChevronLeft className="size-6" aria-hidden />
								Back
							</button>
							<DrawerPrimitive.Title className="min-w-0 truncate text-center text-[17px] font-semibold">
								{page.title}
							</DrawerPrimitive.Title>
							<span />
						</div>
					) : (
						<DrawerPrimitive.Title
							className={cn(
								'shrink-0 px-6 pt-2.5 text-center text-[13px] font-semibold text-muted-foreground',
								hideTitle && 'sr-only',
							)}
						>
							{title}
						</DrawerPrimitive.Title>
					)}
					{showDescription && (
						<DrawerPrimitive.Description className="shrink-0 px-6 pt-0.5 text-center text-[13px] text-muted-foreground">
							{description}
						</DrawerPrimitive.Description>
					)}

					<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2.5 pb-2 pt-2">
						{!page && header}
						{page?.searchable && (
							<label className="mb-2 flex h-10 items-center gap-2 rounded-[10px] bg-zinc-200/70 px-3 dark:bg-zinc-800">
								<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
								<input
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder={page.searchPlaceholder ?? 'Search'}
									aria-label={page.searchPlaceholder ?? 'Search'}
									className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
									autoCapitalize="none"
									autoCorrect="off"
									enterKeyHint="search"
								/>
							</label>
						)}
						{page?.content}
						{visibleSections.map((section, sectionIndex) => (
							<div key={section.id ?? sectionIndex} className={cn(sectionIndex > 0 && 'mt-3')}>
								{section.title && (
									<div className="px-3 pb-1.5 text-[13px] uppercase tracking-wide text-muted-foreground">{section.title}</div>
								)}
								<div role="group" className="overflow-hidden rounded-xl bg-background dark:bg-zinc-900">
									{section.items.map((item, itemIndex) => (
										<ActionSheetRow
											key={item.id ?? itemIndex}
											item={item}
											divider={itemIndex > 0}
											onSelect={() => select(item)}
										/>
									))}
								</div>
							</div>
						))}
						{page?.searchable && query && visibleSections.length === 0 && (
							<p className="px-3 py-6 text-center text-[15px] text-muted-foreground">{page.emptyText ?? 'No matches'}</p>
						)}
					</div>

					{cancelLabel !== false && (
						<div className="shrink-0 px-2.5 pb-[max(0.625rem,var(--safe-bottom))] pt-1">
							<button
								type="button"
								onClick={() => onOpenChange(false)}
								className="h-[52px] w-full rounded-xl bg-background text-[17px] font-semibold text-primary active:bg-muted dark:bg-zinc-900 dark:active:bg-zinc-800"
							>
								{cancelLabel}
							</button>
						</div>
					)}
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

function ActionSheetRow({ item, divider, onSelect }: { item: ActionSheetItem; divider: boolean; onSelect: () => void }) {
	const Icon = item.icon;
	return (
		<button
			type="button"
			disabled={item.disabled}
			aria-pressed={item.checked}
			onClick={onSelect}
			className={cn(
				'relative flex min-h-[52px] w-full items-center gap-3 px-4 py-2 text-left text-[17px] active:bg-muted disabled:opacity-40 dark:active:bg-zinc-800',
				item.destructive ? 'text-red-600 dark:text-red-500' : 'text-foreground',
			)}
		>
			{divider && <span aria-hidden className="absolute inset-x-0 top-0 ml-4 h-px bg-border/70" />}
			{Icon && (
				<Icon
					aria-hidden
					className={cn('size-[22px] shrink-0', item.destructive ? 'text-red-600 dark:text-red-500' : 'text-muted-foreground')}
				/>
			)}
			{item.trailing ? (
				// Label and value share three content-sized tracks, the label spanning
				// two. Each keeps its own width while both fit; when one is short the
				// other takes the rest, and when both are long the label keeps two
				// thirds, so a long value (an email) truncates first, as in iOS Settings
				// rows, without holding space for a short value at larger text sizes.
				<span className="grid min-w-0 flex-1 grid-cols-[repeat(3,minmax(0,max-content))] items-center justify-between gap-x-3">
					<ActionSheetRowLabel item={item} className="col-span-2" />
					<span className="flex min-w-0 justify-end text-[15px] text-muted-foreground">
						<span className="truncate">{item.trailing}</span>
					</span>
				</span>
			) : (
				<ActionSheetRowLabel item={item} className="flex-1" />
			)}
			{item.checked && <Check aria-hidden className="size-5 shrink-0 text-primary" strokeWidth={2.5} />}
			{item.page && <ChevronRight aria-hidden className="size-5 shrink-0 text-muted-foreground/60" />}
		</button>
	);
}

function ActionSheetRowLabel({ item, className }: { item: ActionSheetItem; className: string }) {
	return (
		<span className={cn('min-w-0', className)}>
			<span className="block truncate leading-snug">{item.label}</span>
			{item.description && <span className="block text-[13px] leading-snug text-muted-foreground">{item.description}</span>}
		</span>
	);
}
